import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, signal } from '@angular/core';
import { FieldType, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { debounceTime, distinctUntilChanged, map, merge, skip, startWith, Subscription, takeUntil, tap } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import { DateHelperService } from '../../services/date-helper.service';
import { CronWeekdayChipsComponent } from './cron-weekday-chips.component';
import {
  chipsFromRepeat,
  missingStartWeekday,
  weeklyDaysRequiredValidator,
  weeklyRepeatFromChips,
  weeklyStartDayValidator,
} from './cron-weekly.util';
import { commitUserValue } from './user-edit.util';
import { debugLog } from '../../utils/debug-logger';

/** Subfields the compact weekly style keeps; the rest of the repeat settings are fixed by the style. */
const WEEKLY_STYLE_KEYS = ['startDate', 'startTime', 'endTime'];

/** Depth-first search for the field carrying `keyPath`, across sections and groups. */
export function findFieldByKeyPath(field: FormlyFieldConfig, keyPath: string): FormlyFieldConfig | undefined {
  if (field.props?.['keyPath'] === keyPath) return field;
  for (const child of field.fieldGroup ?? []) {
    const match = findFieldByKeyPath(child, keyPath);
    if (match) return match;
  }
  return undefined;
}

/**
 * Day the series actually runs first: the start date itself, or for a weekly series the first
 * ticked weekday on or after it (start Thursday, only Monday ticked → the Monday after).
 */
export function firstRunDate(startDate: string, repeat: string, daysOfWeek: (string | number)[] | null): Date {
  // A bare 'YYYY-MM-DD' parses as UTC midnight, a day early west of UTC; read it as local time.
  const start = new Date(startDate.includes('T') ? startDate : `${startDate}T00:00:00`);
  start.setHours(0, 0, 0, 0);
  const days = (daysOfWeek ?? []).map(Number);
  if (repeat !== 'Wekelijks' || days.length === 0) return start;
  for (let offset = 0; offset < 7; offset++) {
    const day = DateHelperService.addDays(start, offset);
    if (days.includes(day.getDay())) return day;
  }
  return start;
}

let nextErrorId = 0;

@Component({
  selector: 'formly-field-cron',
  template: `
    <div class="block float-left w-full">
      @if (offset < 0) {
        <mat-error class="pb-4">Let op: De begindatum moet eerder zijn dan de einddatum.</mat-error>
      }
      @if (missingStartDay() && !weeklyStyle()) {
        <mat-error class="pb-4">Let op: de herhaling start op {{ missingStartDay() }}. Vink die dag aan.</mat-error>
      }
      @if (weeklyStyle()) {
        <!-- Figma: the chips left, "Vanaf" right at 244px; stacked below md. -->
        <div class="flex w-full flex-col gap-4 md:flex-row md:items-start" data-cy="cron-weekly-row">
          <div class="min-w-0 md:flex-1">
            <dd-cron-weekday-chips
              [selected]="weeklyDays()"
              [disabled]="weeklyDisabled()"
              [describedBy]="missingStartDay() || noWeekday() ? startDayErrorId : null"
              (selectedChange)="onWeekdaysChange($event)"></dd-cron-weekday-chips>
            @if (noWeekday()) {
              <mat-error
                class="dd-startday-error block pb-4"
                role="alert"
                [id]="startDayErrorId"
                data-cy="cron-noday-error">
                Kies minstens één dag.
              </mat-error>
            } @else if (missingStartDay()) {
              <!-- Under the chips, so the row does not shift; an alert, because it appears while the user ticks. -->
              <mat-error
                class="dd-startday-error block pb-4"
                role="alert"
                [id]="startDayErrorId"
                data-cy="cron-startday-error">
                Let op: de herhaling start op {{ missingStartDay() }}. Vink die dag aan.
              </mat-error>
            }
          </div>
          @if (weeklyStart(); as start) {
            <!-- A wrapper as the flex item: the field host carries the config's own width classes
                 (w-full, float-left; Tailwind !important), which would squeeze the chips to nothing. -->
            <div class="w-full md:w-[244px] md:shrink-0" data-cy="cron-weekly-start">
              <formly-field [field]="start"></formly-field>
            </div>
          }
        </div>
        @for (field of weeklyRest(); track $index) {
          <formly-field [field]="field" [class]="field.props.className"></formly-field>
        }
        <!-- Formly copies control to model only in a rendered formly-field, and a save reads the model:
             the subfields this style does not show (days-of-week, weekly, repeat-type-end, endDate, offset)
             stay rendered, hidden. -->
        <div class="hidden" data-cy="cron-weekly-hidden">
          @for (field of weeklyHidden(); track $index) {
            <formly-field [field]="field"></formly-field>
          }
        </div>
      } @else {
        @for (field of field.fieldGroup; track $index) {
          <formly-field [field]="field" [class]="field.props.className"></formly-field>
        }
      }
      @if (field.props.description) {
        <mat-hint class="inline-block mb-2">{{ field.props.description }}</mat-hint>
      }
    </div>
  `,
  styles: `
    /* A mat-error outside a mat-form-field gets no error colour from Material; the field token has a value per theme. */
    .dd-startday-error {
      color: var(--dd-field-fg-error, var(--mat-sys-error, #d42e11));
    }
  `,
  imports: [
    ReactiveFormsModule,
    MatAutocompleteModule,
    FormlyModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    CronWeekdayChipsComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyFieldCron extends FieldType implements OnInit {
  modulesStore = inject(ModulesStore);
  generatedForms$ = toObservable(this.modulesStore.generatedForms);
  private destroy$ = injectDestroy();
  readonly #cdr = inject(ChangeDetectorRef);
  offset = 0;
  /** Weekday name of a weekly start date that is not among the selected days; the backend rejects that. */
  readonly missingStartDay = signal<string | null>(null);
  /** Weekly style: a start date but no weekday ticked. */
  readonly noWeekday = signal(false);
  /** `props.cronStyle === 'weekly'`: weekday chips + "Vanaf" date instead of the full repeat settings. */
  readonly weeklyStyle = signal(false);
  /** The start date field as rendered in weekly style: a view over the real field with the "Vanaf" label. */
  readonly weeklyStart = signal<FormlyFieldConfig | undefined>(undefined);
  readonly weeklyRest = signal<FormlyFieldConfig[]>([]);
  readonly weeklyHidden = signal<FormlyFieldConfig[]>([]);
  /** Stable per instance: the chips group points `aria-describedby` at it. */
  readonly startDayErrorId = `dd-cron-startday-error-${nextErrorId++}`;
  readonly weeklyDays = signal<(string | number)[]>([]);
  readonly weeklyDisabled = signal(false);
  #daysOfWeekControl: AbstractControl | null = null;
  firstChange = true;
  startDateWatcher: Subscription;
  private readonly fullDayStartTime = '00:00';
  private readonly fullDayEndTime = '23:59';
  private readonly defaultDurationSeconds = 86400; // 24 hours

  constructor() {
    super();
  }

  ngOnInit() {
    this.updateTimecontrollers();
    this.#initWeeklyStyle();

    // Watch for changes in startDate, startTime and endTime
    const startDateControl = this.field.formControl.get('startDate');
    const startTimeControl = this.field.formControl.get('startTime');
    const endTimeControl = this.field.formControl.get('endTime');

    // Combine startDate and startTime
    startTimeControl?.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        tap(startTime => {
          this.updateDateWithTime('startDate', startTime);
        })
      )
      .subscribe();

    // Calculate endDate based on startDate and times
    endTimeControl?.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        tap(() => {
          this.calculateEndDate();
        })
      )
      .subscribe();

    startDateControl?.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        // The Material datepicker (LuxonDateAdapter) and our string normalizer alternately rewrite
        // this control with the SAME instant but a different type (Luxon DateTime <-> ISO string).
        // A reference-based distinctUntilChanged never collapses that, so every rewrite re-enters the
        // handler and patches related fields again -> infinite valueChanges recursion (stack overflow).
        // Deduping on the normalized string makes both representations compare equal and breaks the loop.
        map(emittedValue => this.toComparableDate(emittedValue, this.shouldUseFullDay())),
        distinctUntilChanged(),
        tap(normalized => {
          if (normalized == null) return;
          this.normalizeDateControl(startDateControl, this.shouldUseFullDay());
          // With startDateLeads the day logic runs on a change of DAY only (#leadSourceDate). Here it
          // would also fire on a new start time or on the normalisation of a saved date, and reset a
          // deliberately un-ticked start day.
          if (!this.props['startDateLeads']) this.updateStartDateRelatedFields(startDateControl.value);
          this.calculateEndDate();
        })
      )
      .subscribe();

    this.field.formControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        debounceTime(300),
        tap(values => {
          this.field.formControl.get('offset').patchValue(this.generateOffset(values));
          this.missingStartDay.set(this.findMissingStartDay(values));
          this.noWeekday.set(
            this.weeklyStyle() && !!values.startDate && chipsFromRepeat(values['days-of-week']).length === 0
          );
          this.validateOffset();
          // validateOffset replaced the errors; give the weekly start-day error back.
          if (this.weeklyStyle()) this.#restoreStartDayError();
          this.#cdr.markForCheck();
          // add watcher on value change because of dynamic field creation
          this.createStartDateWatcher();
        })
      )
      .subscribe();

    this.initializeFieldGroup();
    this.applyFullDayTimeDefaults();

    // check the related checkboxes if startDate is set (prefill), but no days-of-week are selected
    if (
      this.field.formControl.value.startDate != null &&
      this.field.formControl.get('days-of-week')?.value?.length === 0
    ) {
      this.updateStartDateRelatedFields(this.field.formControl.value.startDate);
    }

    this.syncStartDateFromField();
    this.missingStartDay.set(this.findMissingStartDay(this.field.formControl.value));
  }

  /**
   * `startDateField` names another date field (by keyPath) whose value is the start of the series,
   * such as a create form's departure date. Its value is copied into startDate on every change, so
   * the startDate subfield can be hidden and the weekday logic below still has a date to work with.
   */
  private syncStartDateFromField() {
    const keyPath = this.props['startDateField'];
    if (!keyPath) return;
    const sourceField = findFieldByKeyPath(this.#rootField(), keyPath);
    const sourceControl = sourceField?.formControl;
    const startDateControl = this.field.formControl.get('startDate');
    if (!sourceControl || !startDateControl) return;

    if (this.props['startDateLeads']) {
      this.#leadSourceDate(sourceField, startDateControl);
      return;
    }

    // A saved series keeps its own start date on open; only an empty one is seeded from the source.
    const initial$ = startDateControl.value == null ? [sourceControl.value] : [];

    sourceControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        startWith(...initial$),
        // The day only: the source's time of day would overrule the series' own Van-time and could
        // put startDate after endDate (negative offset).
        map(value => this.toComparableDate(value, true)),
        distinctUntilChanged()
      )
      .subscribe(startDate => {
        if (startDate == null) return;
        startDateControl.patchValue(startDate);
        this.updateDateWithTime('startDate', this.field.formControl.get('startTime')?.value);
        this.addStartWeekday(startDate);
      });
  }

  /**
   * `startDateLeads`: the opposite direction. The series' start date is what the planner picks, and
   * the source field (the departure) shows only its time; its date is kept on the first run day in
   * the background, so everything that reads the departure — the route's stop dates, the action that
   * sends `repeat.startDate` — gets a real date that the backend accepts (it lies on a ticked day).
   * Hiding the cron (switching back to a one-off order) destroys it and gives the date back.
   */
  #leadSourceDate(sourceField: FormlyFieldConfig, startDateControl: AbstractControl) {
    const sourceControl = sourceField.formControl;
    const props = sourceField.props;
    const hideDateBefore = props.hideDate;
    // The date is hidden, so a stored date alone must not count as filled in (see FormlyFieldDateTime).
    const timeRequired = (control: AbstractControl) =>
      props.required && !DateHelperService.dateTimeToDateAndTime(control.value ?? null).time
        ? { required: true }
        : null;
    props.hideDate = true;
    sourceControl.addValidators(timeRequired);
    sourceControl.updateValueAndValidity({ emitEvent: false });
    this.destroy$.subscribe(() => {
      props.hideDate = hideDateBefore;
      sourceControl.removeValidators(timeRequired);
      sourceControl.updateValueAndValidity({ emitEvent: false });
    });

    // An empty series takes its start from a departure that is already there (prefill, loaded order).
    if (startDateControl.value == null && sourceControl.value) {
      startDateControl.patchValue(this.toComparableDate(sourceControl.value, true));
    }

    // Default: the start day is ticked. Only on a new start date, so un-ticking it afterwards sticks
    // and the first run moves to the next ticked day instead. Listen on the cron group, not on its
    // children: the subfields are created dynamically and a child control can be swapped out.
    const cronValue$ = this.field.formControl.valueChanges.pipe(takeUntil(this.destroy$));
    cronValue$
      .pipe(
        map(value => this.toComparableDate(value?.startDate, true)),
        // Seeded with the current date, so the first emission (often a day being un-ticked) does not
        // count as a new start date and re-tick it.
        startWith(this.toComparableDate(startDateControl.value, true)),
        distinctUntilChanged(),
        skip(1)
      )
      .subscribe(startDate => {
        if (!startDate) return;
        this.updateStartDateRelatedFields(startDate);
        this.addStartWeekday(startDate);
      });

    merge(cronValue$, sourceControl.valueChanges)
      .pipe(takeUntil(this.destroy$), startWith(null), debounceTime(0))
      .subscribe(() => {
        const { startDate: rawStart, repeat, 'days-of-week': daysOfWeek } = this.field.formControl.value ?? {};
        const startDate = this.toComparableDate(rawStart, true);
        if (!startDate) return;
        // A weekly series with no day ticked (seeded before the repeat default existed, or the section
        // was hidden and Formly cleared it) runs on its start day. The patch re-emits and lands here again.
        if (repeat === 'Wekelijks' && !daysOfWeek?.length) {
          this.field.formControl.get('days-of-week')?.patchValue([new Date(startDate).getDay().toString()]);
          return;
        }
        const runDate = firstRunDate(startDate, repeat, daysOfWeek);
        const { date, time } = DateHelperService.dateTimeToDateAndTime(sourceControl.value ?? null);
        const day = DateHelperService.dateObjectToDateString(runDate);
        if (date === day) return;
        if (time) {
          const [hours, minutes] = time.split(':').map(Number);
          runDate.setHours(hours, minutes, 0, 0);
        }
        sourceControl.setValue(time ? DateHelperService.toLocalISOString(runDate) : day);
      });
  }

  /**
   * A synced start date can change after the planner already picked several days. The single-day
   * case is handled by updateStartDateRelatedFields; here the start day is added to a longer
   * selection, because the backend refuses a weekly series that does not run on its first date.
   */
  private addStartWeekday(startDate: string) {
    const daysOfWeek = this.field.formControl.get('days-of-week');
    const selected = (daysOfWeek?.value ?? []) as (string | number)[];
    if (this.field.formControl.get('repeat')?.value !== 'Wekelijks' || selected.length < 2) return;
    const startDay = new Date(startDate).getDay().toString();
    if (!selected.map(String).includes(startDay)) {
      daysOfWeek.patchValue([...selected.map(String), startDay]);
    }
  }

  private findMissingStartDay(values: { startDate?: string; repeat?: string; 'days-of-week'?: (string | number)[] }) {
    // With startDateLeads an unticked start day is allowed: the first run moves to the next ticked day.
    if (this.props['startDateLeads']) return null;
    if (values?.repeat !== 'Wekelijks') return null;
    return missingStartWeekday(values.startDate, values['days-of-week']);
  }

  /** The group's own validator runs on every change; `validateOffset` setErrors wipes its result after the debounce. */
  #restoreStartDayError() {
    const group = this.field.formControl;
    const errors = {
      ...weeklyDaysRequiredValidator(group),
      // Only where the validator is attached: with startDateLeads an unticked start day is allowed.
      ...(group.hasValidator(weeklyStartDayValidator) ? weeklyStartDayValidator(group) : null),
    };
    if (Object.keys(errors).length) group.setErrors({ ...group.errors, ...errors });
  }

  onWeekdaysChange(days: string[]) {
    if (this.#daysOfWeekControl?.disabled) return;
    commitUserValue(this.#daysOfWeekControl, days);
  }

  /**
   * Compact weekly style: only the start date (as "Vanaf"), the times and the weekday chips show; the
   * repeat object is a weekly series without end date, written to the same controls the full style uses.
   * It only FILLS: a stored value is never overwritten, and nothing here marks the form dirty (the chips
   * do that on a user toggle). A stored series of another kind (Dagelijks, Maandelijks, ...) cannot be
   * shown as chips, so that entry keeps the full style.
   */
  #initWeeklyStyle() {
    if (this.props['cronStyle'] !== 'weekly') return;
    const group = this.field.formControl;
    const repeat = group.get('repeat');
    const stored = repeat?.value;
    // Any stored kind but weekly keeps the full style: it cannot be shown as chips. 'no-repeat' is the
    // shared CRON_FIELDS default and counts as unset, as in applyNoRepeatRemoval.
    if (stored && stored !== 'Wekelijks' && stored !== 'no-repeat') {
      console.warn(`[cron] cronStyle 'weekly' ignored on '${String(this.field.key)}': stored repeat is '${stored}'`);
      return;
    }
    // The weekly style has no end controls: a series ending on a date or after a count keeps the full style.
    const storedEnd = group.get('repeat-type-end')?.value;
    if (storedEnd && storedEnd !== 'no-end-date') {
      console.warn(`[cron] cronStyle 'weekly' ignored on '${String(this.field.key)}': stored end is '${storedEnd}'`);
      return;
    }
    // Nor an interval: chips mean every week, so "every 2 weeks" would be saved without being shown.
    const storedInterval = group.get('weekly')?.value;
    if (storedInterval && String(storedInterval) !== '1') {
      console.warn(
        `[cron] cronStyle 'weekly' ignored on '${String(this.field.key)}': stored interval is '${storedInterval}'`
      );
      return;
    }
    // Set the series to weekly first: tenant configs show `days-of-week` only under the condition
    // "repeat == Wekelijks", so before this its control is not registered yet.
    // Model too: conditions read the model, and the repeat subfield syncs it only once rendered.
    const restore = () => {
      repeat?.patchValue(stored, { emitEvent: false });
      if (!this.model) return;
      if (stored === undefined) delete this.model['repeat'];
      else this.model['repeat'] = stored;
    };
    repeat?.patchValue('Wekelijks', { emitEvent: false });
    if (this.model) this.model['repeat'] = 'Wekelijks';
    this.options?.checkExpressions?.(this.#rootField());
    const daysOfWeek = group.get('days-of-week');
    if (!daysOfWeek) {
      restore();
      this.options?.checkExpressions?.(this.#rootField());
      console.warn(`[cron] cronStyle 'weekly' needs a 'days-of-week' subfield on '${String(this.field.key)}'`);
      return;
    }
    this.#daysOfWeekControl = daysOfWeek;

    // Fill only what is empty.
    const defaults = weeklyRepeatFromChips(daysOfWeek.value);
    // Control and model both: the model is what a save reads, and no rendered field syncs these two yet.
    const fill = (key: 'weekly' | 'repeat-type-end') => {
      const control = group.get(key);
      if (!control || control.value) return;
      control.patchValue(defaults[key], { emitEvent: false });
      if (this.model) this.model[key] = defaults[key];
    };
    fill('weekly');
    fill('repeat-type-end');

    const kept = (this.field.fieldGroup ?? []).filter(field => WEEKLY_STYLE_KEYS.includes(field.key as string));
    const startField = kept.find(field => field.key === 'startDate');
    // A view over the real field (prototype link keeps its control, parent and hooks): only the label
    // and placeholder differ, and nothing on the shared config is touched. Writes to props go to the view only (not supported).
    // ponytail: Formly's render bookkeeping (_componentRefs etc.) lands as own props on the view and
    // the view is not in parent.fieldGroup; read from 7.1.0, the live-view spec is the tripwire on a bump.
    const own = { writable: true, configurable: true, enumerable: true };
    const labelProps =
      startField &&
      Object.create(startField.props ?? null, {
        label: { value: 'Vanaf', ...own },
        // Figma: the format as placeholder in the field; the date field then drops its hint line.
        placeholder: { value: 'DD-MM-JJJJ', ...own },
      });
    // Formly redefines `props` with observers, so it must stay configurable.
    this.weeklyStart.set(startField && Object.create(startField, { props: { value: labelProps, ...own } }));
    this.weeklyRest.set(kept.filter(field => field !== startField));
    this.weeklyHidden.set((this.field.fieldGroup ?? []).filter(field => !kept.includes(field)));

    this.weeklyDays.set(daysOfWeek.value ?? []);
    daysOfWeek.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(days => this.weeklyDays.set(days ?? []));
    // A disabled repeat (no edit right for this role) shows its days but takes no toggle.
    this.weeklyDisabled.set(daysOfWeek.disabled);
    daysOfWeek.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.weeklyDisabled.set(daysOfWeek.disabled));

    group.addValidators(weeklyDaysRequiredValidator);
    this.destroy$.subscribe(() => group.removeValidators(weeklyDaysRequiredValidator));
    // With startDateLeads an unticked start day is allowed (the first run moves), so no validator.
    if (!this.props['startDateLeads']) {
      group.addValidators(weeklyStartDayValidator);
      group.updateValueAndValidity({ emitEvent: false });
      this.destroy$.subscribe(() => group.removeValidators(weeklyStartDayValidator));
    }
    this.weeklyStyle.set(true);
    debugLog('cron', 'weekly style', { days: daysOfWeek.value });
  }

  #rootField(): FormlyFieldConfig {
    let root: FormlyFieldConfig = this.field;
    while (root.parent) root = root.parent;
    return root;
  }

  private updateTimecontrollers() {
    if (this.shouldUseFullDay()) {
      this.applyFullDayTimeDefaults();
      return;
    }

    if (
      this.field.formControl.get('startTime').value == null &&
      this.field.formControl.get('startDate').value != null
    ) {
      const date = new Date(this.field.formControl.get('startDate').value);
      const dateAndTime = DateHelperService.dateTimeToDateAndTime(DateHelperService.toLocalISOString(date));
      this.field.formControl.get('startTime').patchValue(dateAndTime.time);
      this.normalizeDateControl(this.field.formControl.get('startDate'));
    }

    if (this.field.formControl.get('endTime').value == null && this.field.formControl.get('endDate').value != null) {
      const date = new Date(this.field.formControl.get('endDate').value);
      const dateAndTime = DateHelperService.dateTimeToDateAndTime(DateHelperService.toLocalISOString(date));
      this.field.formControl.get('endTime').patchValue(dateAndTime.time);
      this.normalizeDateControl(this.field.formControl.get('endDate'));
    }

    if (this.field.formControl.get('endDate').value == null && this.field.formControl.get('endTime').value != null) {
      this.calculateEndDate();
    }
  }

  private updateDateWithTime(dateField: string, timeString: string) {
    const dateControl = this.field.formControl.get(dateField);
    if (!dateControl?.value || !timeString) return;

    const date = new Date(dateControl.value);
    const [hours, minutes] = timeString.split(':').map(Number);

    date.setHours(hours, minutes, 0, 0);
    dateControl.patchValue(DateHelperService.toLocalISOString(date));
  }

  private calculateEndDate() {
    const startDate = this.field.formControl.get('startDate')?.value;
    const startTime = this.field.formControl.get('startTime')?.value;
    const endTime = this.field.formControl.get('endTime')?.value;

    if (!startDate || !startTime || !endTime) return;

    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);

    const startDateTime = new Date(startDate);
    startDateTime.setHours(startHours, startMinutes, 0, 0);

    const endDateTime = new Date(startDate);
    endDateTime.setHours(endHours, endMinutes, 0, 0);

    // If end time is earlier than start time, add one day to end date
    if (endHours < startHours || (endHours === startHours && endMinutes <= startMinutes)) {
      endDateTime.setDate(endDateTime.getDate() + 1);
    }

    this.field.formControl.get('endDate')?.patchValue(DateHelperService.toLocalISOString(endDateTime));
  }

  private initializeFieldGroup() {
    this.field.fieldGroup = this.field.fieldGroup.map(field => {
      if (this.field.props.required) {
        field.props.required = this.field.props.required;
      }

      // A subfield the form builder hid (`hiddenSubfields`) can never be filled in, so it cannot be
      // required either — its value comes from elsewhere (startDateField, an action mapping). Read
      // from the config rather than the `hidden` class: CRON_FIELDS hides endDate by default too.
      if ((this.props['hiddenSubfields'] as string[] | undefined)?.includes(field.key as string)) {
        field.props.required = false;
      }

      if (this.props.cronRequired === true && field.key === 'repeat') {
        this.removeNoRepeatOption(field);
      }

      if (this.props.dateRange === false) {
        if (field.key === 'endDate') {
          field.className = 'hidden';
          field.props.required = false;
        }
        if (field.key === 'startDate') {
          field.className = 'w-full inline-block';
        }
      }

      if (this.shouldUseFullDay() && (field.key === 'startTime' || field.key === 'endTime')) {
        field.className = 'hidden';
        field.props.required = false;
      }

      if (this.props.endTimeOptional === true && field.key === 'endTime') {
        field.props.required = false;
      }

      return field;
    });
  }

  removeNoRepeatOption(field) {
    const moduleId = this.options.formState?.entity?.id;

    // A create-form page has no entity in its formState, so there is no generated form to follow;
    // without this the option stayed and the field opened on 'Geen herhaling'.
    if (!moduleId) {
      this.applyNoRepeatRemoval(field);
      return;
    }

    this.generatedForms$
      .pipe(
        takeUntil(this.destroy$),
        map(forms => forms[moduleId]),
        distinctUntilChanged((prev, curr) => {
          if (this.firstChange) {
            this.firstChange = false;
            return false;
          }
          return JSON.stringify(prev) === JSON.stringify(curr);
        }),
        tap(() => this.applyNoRepeatRemoval(field))
      )
      .subscribe();
  }

  private applyNoRepeatRemoval(field) {
    const withoutNoRepeat = (options: Array<{ value: any; label: string }> = []) =>
      options.filter(option => option.value !== 'no-repeat');
    field.props.options = withoutNoRepeat(field.props.options as Array<{ value: any; label: string }>);
    // custom-select builds props.options from optionsArray, possibly after this ran (the options
    // were still empty on the create-form page), so the source has to lose the option as well.
    // Copied, not mutated: the generator spreads subfields shallowly, so these objects can be the
    // shared CRON_FIELDS defaults, and stripping them would drop 'Geen herhaling' for every cron.
    field.props.optionsArray = (
      (field.props.optionsArray ?? []) as Array<{ options?: Array<{ value: any; label: string }> }>
    ).map(source => ({ ...source, options: withoutNoRepeat(source.options) }));

    // Only set defaults when the current value is missing or invalid ('no-repeat'),
    // so existing saved values are preserved when editing entries
    const currentRepeat = this.field.formControl.get(field.key)?.value;
    if (!currentRepeat || currentRepeat === 'no-repeat') {
      this.field.formControl.get(field.key)?.patchValue('Wekelijks');
    }

    const currentRepeatTypeEnd = this.field.formControl.get('repeat-type-end')?.value;
    if (!currentRepeatTypeEnd) {
      this.field.formControl.get('repeat-type-end')?.patchValue('no-end-date');
    }
  }

  createStartDateWatcher() {
    if (this.startDateWatcher) this.startDateWatcher.unsubscribe();
    if (this.field.formControl.get('repeat-type') === null) {
      return;
    }

    this.startDateWatcher = this.field.formControl
      .get('repeat-type')
      .valueChanges.pipe(
        takeUntil(this.destroy$),
        tap(() => {
          requestAnimationFrame(() => {
            this.updateStartDateRelatedFields(this.field.formControl.value.startDate);
          });
        })
      )
      .subscribe();
  }

  updateStartDateRelatedFields(startDate) {
    if (startDate === undefined) {
      return;
    }

    startDate = new Date(startDate);
    const startDayOfWeek = startDate.getDay();
    const startDayOfMonth = startDate.getDate();
    const startMonth = startDate.getMonth() + 1; // Months are 0-indexed

    const repeatType = this.field.formControl.get('repeat').value;

    if (repeatType === 'Wekelijks') {
      // Initialize weekly interval to 1 if not set
      const weeklyControl = this.field.formControl.get('weekly');
      if (weeklyControl && !weeklyControl.value) {
        weeklyControl.patchValue('1');
      }

      // Initialize days-of-week based on startDate (only when not explicitly multi-selected)
      const daysOfWeek = this.field.formControl.get('days-of-week');
      if (daysOfWeek && (daysOfWeek.value?.length === 0 || daysOfWeek.value?.length === 1)) {
        this.patchArrayIfChanged('days-of-week', [startDayOfWeek.toString()]);
      }
    } else if (repeatType === 'Maandelijks' && this.field.formControl.get('days-of-month')) {
      this.patchArrayIfChanged('days-of-month', [startDayOfMonth.toString()]);
    } else if (repeatType === 'Jaarlijks' && this.field.formControl.get('months-of-year')) {
      this.patchArrayIfChanged('months-of-year', [startMonth.toString()]);
    }
  }

  /**
   * Patch an array control only when the value actually changes, so an unchanged re-computation
   * does not re-emit valueChanges and pump the parent form group (which re-hydrates the datepicker).
   */
  private patchArrayIfChanged(controlName: string, next: string[]) {
    const control = this.field.formControl.get(controlName);
    if (!control) return;
    const current = control.value as (string | number)[] | null;
    if (
      Array.isArray(current) &&
      current.length === next.length &&
      current.every((value, index) => String(value) === next[index])
    ) {
      return;
    }
    control.patchValue(next);
  }

  generateOffset(values: any): string {
    // generate offset in s startDate - endDate
    if (!values.startDate) {
      return '0';
    }

    // When endTime is optional and not provided, default to 24 hours
    if (!values.endDate && this.props.endTimeOptional === true) {
      this.offset = this.defaultDurationSeconds;
      return this.defaultDurationSeconds.toString();
    }

    if (!values.endDate) {
      return '0';
    }

    const startDate = new Date(values.startDate);
    const endDate = new Date(values.endDate);
    const offset = (endDate.getTime() - startDate.getTime()) / 1000;

    this.offset = offset;
    return offset.toString();
  }

  validateOffset() {
    if (this.props.dateRange === false) {
      this.field.formControl.setErrors(null);
    }

    if (this.offset < 0) {
      this.field.formControl.setErrors({ invalidOffset: true });
    } else {
      this.field.formControl.setErrors(null);
    }
  }

  private shouldUseFullDay(): boolean {
    return this.props.fullDay === true;
  }

  /**
   * Normalize a date form control value to a local ISO string without timezone.
   * Handles Luxon DateTime objects (from datepicker) and timezone strings (from API).
   * Returns true if the value was changed (patched), false if already normalized.
   */
  private normalizeDateControl(control: AbstractControl, resetToMidnight = false): boolean {
    if (!control?.value) return false;
    const raw = control.value?.ts != null ? control.value.ts : control.value;
    const date = new Date(raw);
    if (resetToMidnight) {
      date.setHours(0, 0, 0, 0);
    }
    const normalized = DateHelperService.toLocalISOString(date);
    if (control.value !== normalized) {
      // Same calendar day, so the input text needs no rewrite; rewriting it would move the
      // caret and reformat a date that is still being typed (#22991).
      control.patchValue(normalized, { emitModelToViewChange: false });
      return true;
    }
    return false;
  }

  /**
   * Map a date control value to a single comparable string. The picker emits Luxon DateTime
   * objects while the API/normalizer use ISO strings; both are reduced to the same local-ISO
   * string so distinctUntilChanged can collapse the Luxon<->string type ping-pong.
   */
  private toComparableDate(value: unknown, resetToMidnight = false): string | null {
    if (value == null) return null;
    const raw =
      (value as { ts?: number })?.ts != null ? (value as { ts: number }).ts : (value as string | number | Date);
    const date = new Date(raw);
    if (isNaN(date.getTime())) return null;
    if (resetToMidnight) {
      date.setHours(0, 0, 0, 0);
    }
    return DateHelperService.toLocalISOString(date);
  }

  private applyFullDayTimeDefaults() {
    if (!this.shouldUseFullDay()) {
      return;
    }

    const startTimeControl = this.field.formControl.get('startTime');
    const endTimeControl = this.field.formControl.get('endTime');

    if (startTimeControl && startTimeControl.value !== this.fullDayStartTime) {
      startTimeControl.patchValue(this.fullDayStartTime);
    }

    if (endTimeControl && endTimeControl.value !== this.fullDayEndTime) {
      endTimeControl.patchValue(this.fullDayEndTime);
    }

    // Normalize startDate to strip timezone (endDate is normalized by calculateEndDate)
    this.normalizeDateControl(this.field.formControl.get('startDate'), true);

    this.calculateEndDate();
  }
}
